import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "@/i18n";

const CONSENT_KEY = "anvoriq_cookie_consent_v1";

function applyConsent(prefs) {
  if (typeof window.gtag !== "function") return;
  window.gtag("consent", "update", {
    ad_storage: prefs.marketing ? "granted" : "denied",
    ad_user_data: prefs.adUserData ? "granted" : "denied",
    ad_personalization: prefs.marketing ? "granted" : "denied",
    analytics_storage: prefs.analytics ? "granted" : "denied",
    functionality_storage: "granted",
    security_storage: "granted",
  });
}

export default function CookieBanner() {
  const { t } = useTranslation();
  const { lang: paramLang } = useParams();
  const lang = SUPPORTED_LANGS.includes(paramLang) ? paramLang : DEFAULT_LANG;
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    adUserData: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      setVisible(true);
    } else {
      try {
        const parsed = JSON.parse(stored);
        applyConsent(parsed);
      } catch (_e) {
        setVisible(true);
      }
    }
    const openHandler = () => {
      const cur = localStorage.getItem(CONSENT_KEY);
      if (cur) {
        try { setPrefs({ necessary: true, ...JSON.parse(cur) }); } catch (_e) {}
      }
      setShowDetails(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", openHandler);
    return () => window.removeEventListener("open-cookie-settings", openHandler);
  }, []);

  const persist = (next) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(next));
    applyConsent(next);
    setVisible(false);
    setShowDetails(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 border-t"
      style={{ backgroundColor: "var(--anv-ink)", borderColor: "rgba(255,255,255,0.1)" }}
      role="dialog"
      aria-live="polite"
      aria-label={t("cookieBanner.title")}
      data-testid="cookie-banner"
    >
      <div className="anv-container py-5 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
          <div className="lg:col-span-8 text-white">
            <div className="font-display text-lg md:text-xl font-semibold">
              {t("cookieBanner.title")}
            </div>
            <p className="mt-2 text-sm text-white/75 leading-relaxed">
              {t("cookieBanner.description")}{" "}
              <Link
                to={`/${lang}/cookie-policy`}
                className="underline text-white hover:text-[color:var(--anv-terra)]"
                data-testid="cookie-banner-policy-link"
              >
                {t("cookieBanner.cookiePolicyLink")}
              </Link>
              .
            </p>

            {showDetails && (
              <div className="mt-4 space-y-3" data-testid="cookie-banner-details">
                {[
                  { key: "necessary", locked: true },
                  { key: "analytics" },
                  { key: "marketing" },
                  { key: "adUserData" },
                ].map((c) => (
                  <div
                    key={c.key}
                    className="flex items-start justify-between gap-4 border border-white/10 p-3"
                    style={{ backgroundColor: "rgba(255,255,255,0.03)" }}
                  >
                    <div>
                      <div className="text-sm font-semibold text-white">
                        {t(`cookieBanner.categories.${c.key}.title`)}
                      </div>
                      <div className="text-xs text-white/70 mt-1">
                        {t(`cookieBanner.categories.${c.key}.description`)}
                      </div>
                    </div>
                    <div className="text-xs text-white/70 whitespace-nowrap pt-1">
                      {c.locked ? (
                        <span data-testid={`cookie-cat-${c.key}-locked`}>
                          {t("cookieBanner.categories.necessary.status")}
                        </span>
                      ) : (
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={prefs[c.key]}
                            onChange={(e) =>
                              setPrefs((p) => ({ ...p, [c.key]: e.target.checked }))
                            }
                            data-testid={`cookie-cat-${c.key}-toggle`}
                          />
                          <span>{prefs[c.key] ? "ON" : "OFF"}</span>
                        </label>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="lg:col-span-4 flex flex-col md:flex-row lg:flex-col gap-2">
            {!showDetails && (
              <button
                type="button"
                onClick={() => setShowDetails(true)}
                className="anv-btn-ghost-dark w-full"
                data-testid="cookie-customize"
              >
                {t("cookieBanner.customize")}
              </button>
            )}
            {showDetails && (
              <button
                type="button"
                onClick={() => persist(prefs)}
                className="anv-btn-ghost-dark w-full"
                data-testid="cookie-save"
              >
                {t("cookieBanner.save")}
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                persist({ necessary: true, analytics: false, marketing: false, adUserData: false })
              }
              className="anv-btn-ghost-dark w-full"
              data-testid="cookie-reject"
            >
              {t("cookieBanner.rejectAll")}
            </button>
            <button
              type="button"
              onClick={() =>
                persist({ necessary: true, analytics: true, marketing: true, adUserData: true })
              }
              className="anv-btn-primary w-full"
              data-testid="cookie-accept-all"
            >
              {t("cookieBanner.acceptAll")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
